import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Trash2, Shield, Loader2, Crown, Sparkles } from "lucide-react";
import { format } from "date-fns";

interface AdminUser {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  email?: string;
}

export function AdminUserManager() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  // Fetch all admin users with their emails
  const { data: adminUsers = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('id, user_id, role, created_at')
        .eq('role', 'admin')
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch emails from profiles for each admin user
      const usersWithEmails = await Promise.all(
        (roles || []).map(async (role) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('email')
            .eq('id', role.user_id)
            .single();
          
          return {
            ...role,
            email: profile?.email || 'Unknown'
          };
        })
      );

      return usersWithEmails;
    },
  });

  // Get current user ID to prevent self-removal
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const addAdminMutation = useMutation({
    mutationFn: async (email: string) => {
      // Find user by email in profiles
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('email', email.toLowerCase().trim())
        .single();

      if (profileError || !profile) {
        throw new Error('User not found. They must have an account first.');
      }

      // Check if already admin
      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', profile.id)
        .eq('role', 'admin')
        .single();

      if (existingRole) {
        throw new Error('User is already an admin.');
      }

      // Add admin role
      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: profile.id,
          role: 'admin',
          created_by: currentUser?.id
        });

      if (error) throw error;

      return profile;
    },
    onSuccess: (profile) => {
      toast({
        title: "Admin added",
        description: `${profile.email} is now an admin.`,
      });
      setNewAdminEmail("");
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to add admin",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const removeAdminMutation = useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', roleId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Admin removed",
        description: "User no longer has admin access.",
      });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to remove admin",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) return;
    setIsAdding(true);
    await addAdminMutation.mutateAsync(newAdminEmail);
    setIsAdding(false);
  };

  const handleRemoveAdmin = async (roleId: string, userId: string) => {
    if (userId === currentUser?.id) {
      toast({
        title: "Cannot remove yourself",
        description: "You cannot remove your own admin access.",
        variant: "destructive",
      });
      return;
    }

    if (confirm("Remove admin access from this user?")) {
      removeAdminMutation.mutate(roleId);
    }
  };

  // Find the first admin (superadmin) - the one with the earliest created_at
  const superAdminId = adminUsers[0]?.user_id;
  const isSuperAdmin = currentUser?.id === superAdminId;

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
      <CardHeader className="relative">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text">
              Admin Access Control
            </CardTitle>
            <CardDescription>
              Manage who has access to the Command Center. The first admin is the superadmin.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative space-y-4">
        {/* Add new admin */}
        <div className="flex gap-2">
          <Input
            placeholder="Enter email address..."
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddAdmin()}
            className="flex-1 bg-background/50 border-border/50"
          />
          <Button 
            onClick={handleAddAdmin} 
            disabled={isAdding || !newAdminEmail.trim()}
            className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-lg"
          >
            {isAdding ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <UserPlus className="h-4 w-4 mr-2" />
            )}
            Add Admin
          </Button>
        </div>

        {/* Admin users list */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : adminUsers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No admin users configured.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border/30">
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adminUsers.map((admin, index) => (
                <TableRow key={admin.id} className="border-border/30 hover:bg-primary/5">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {admin.email}
                      {admin.user_id === currentUser?.id && (
                        <Badge variant="outline" className="text-xs border-accent/50 text-accent">You</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {index === 0 ? (
                      <Badge className="bg-gradient-to-r from-warning to-destructive text-warning-foreground border-0 shadow-sm">
                        <Crown className="h-3 w-3 mr-1" />
                        Superadmin
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Admin
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(admin.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    {/* Only superadmin can remove others, and no one can remove themselves or superadmin */}
                    {isSuperAdmin && index !== 0 && admin.user_id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAdmin(admin.id, admin.user_id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!isSuperAdmin && adminUsers.length > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            Only the superadmin can add or remove other admins.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
